import type { CardData } from '../../../types/card';
import type { BlueprintStage } from '../../../types/processBlueprint';

type UnknownRecord = Record<string, unknown>;

export interface StageEngineeringMetrics {
  tpStandard: number | null;
  traStandard: number | null;
  expectedErrorPercent: number | null;
  correctionTimeStandard: number | null;
  warningThresholdPercent: number;
  wipLimit: number | null;
  requireApproval: boolean;
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  return null;
}

function firstNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
}

function firstBoolean(...values: unknown[]): boolean | null {
  for (const value of values) {
    const parsed = toBoolean(value);
    if (parsed !== null) return parsed;
  }
  return null;
}

function deriveExpectedErrorPercent(accuracy: unknown): number | null {
  const parsed = toFiniteNumber(accuracy);
  if (parsed === null) return null;
  return Math.min(100, Math.max(0, 100 - parsed));
}

export function inferStageEngineeringFromCard(card: CardData): Pick<
  BlueprintStage,
  | 'tpStandard'
  | 'traStandard'
  | 'expectedErrorPercent'
  | 'correctionTimeStandard'
  | 'warningThresholdPercent'
  | 'wipLimit'
  | 'requireApproval'
> {
  return {
    tpStandard: firstNumber(card.lt, card.tp) ?? undefined,
    traStandard: firstNumber(card.tt) ?? undefined,
    expectedErrorPercent: deriveExpectedErrorPercent(card.percentCompleteAccurate) ?? undefined,
    correctionTimeStandard: firstNumber(card.correctionTime) ?? undefined,
    warningThresholdPercent: 80,
    wipLimit: undefined,
    requireApproval: card.executionConfig?.actionType === 'approve_reject',
  };
}

export function resolveStageEngineering(
  stage: Partial<BlueprintStage> & { metadata?: Record<string, unknown> | null },
): StageEngineeringMetrics {
  const metadata = isRecord(stage.metadata) ? stage.metadata : {};

  return {
    tpStandard:
      firstNumber(
        stage.tpStandard,
        metadata.tp_standard,
        metadata.tpStandard,
        metadata.lt,
        stage.slaHours !== undefined && stage.slaHours !== null ? Number(stage.slaHours) * 60 : null,
      ),
    traStandard: firstNumber(stage.traStandard, metadata.tra_standard, metadata.traStandard, metadata.tt),
    expectedErrorPercent: firstNumber(
      stage.expectedErrorPercent,
      metadata.expected_error_percent,
      metadata.expectedErrorPercent,
      deriveExpectedErrorPercent(metadata.percentCompleteAccurate),
    ),
    correctionTimeStandard: firstNumber(
      stage.correctionTimeStandard,
      metadata.correction_time_standard,
      metadata.correctionTimeStandard,
    ),
    warningThresholdPercent:
      firstNumber(
        stage.warningThresholdPercent,
        metadata.warning_threshold_percent,
        metadata.warningThresholdPercent,
        stage.notifications?.onSlaWarningPercent,
        80,
      ) ?? 80,
    wipLimit: firstNumber(stage.wipLimit, metadata.wip_limit, metadata.wipLimit),
    requireApproval:
      firstBoolean(
        stage.requireApproval,
        stage.rules?.requireApproval,
        metadata.require_approval,
        metadata.requireApproval,
        stage.completionType === 'approve_reject',
      ) ?? false,
  };
}

export function buildBlueprintStageSnapshot(stage: BlueprintStage): Record<string, unknown> {
  const metrics = resolveStageEngineering(stage);
  const metadata = isRecord(stage.metadata) ? stage.metadata : {};

  return {
    order: stage.order,
    name: stage.name,
    description: stage.description ?? null,
    slaHours: stage.slaHours ?? null,
    role: stage.role ?? null,
    checklist: stage.checklist ?? [],
    formFields: stage.formFields ?? [],
    rules: stage.rules ?? {},
    notifications: stage.notifications ?? {},
    metadata: {
      ...metadata,
      tp_standard: metrics.tpStandard,
      tra_standard: metrics.traStandard,
      expected_error_percent: metrics.expectedErrorPercent,
      correction_time_standard: metrics.correctionTimeStandard,
      warning_threshold_percent: metrics.warningThresholdPercent,
      wip_limit: metrics.wipLimit,
      require_approval: metrics.requireApproval,
    },
    sourceNodeId: stage.sourceNodeId ?? null,
    executionConfig: stage.executionConfig ?? null,
    instruction: stage.instruction ?? null,
    completionType: stage.completionType ?? null,
    exitOptions: stage.exitOptions ?? null,
    tp_standard: metrics.tpStandard,
    tra_standard: metrics.traStandard,
    expected_error_percent: metrics.expectedErrorPercent,
    correction_time_standard: metrics.correctionTimeStandard,
    warning_threshold_percent: metrics.warningThresholdPercent,
    wip_limit: metrics.wipLimit,
    require_approval: metrics.requireApproval,
  };
}

export function buildBlueprintSnapshotSummary(stages: Array<Record<string, unknown>>) {
  const tpValues = stages.map((stage) => toFiniteNumber(stage.tp_standard)).filter((value): value is number => value !== null);
  const traValues = stages.map((stage) => toFiniteNumber(stage.tra_standard)).filter((value): value is number => value !== null);
  const errorValues = stages
    .map((stage) => toFiniteNumber(stage.expected_error_percent))
    .filter((value): value is number => value !== null);
  const correctionValues = stages
    .map((stage) => toFiniteNumber(stage.correction_time_standard))
    .filter((value): value is number => value !== null);
  const thresholdValues = stages
    .map((stage) => toFiniteNumber(stage.warning_threshold_percent))
    .filter((value): value is number => value !== null);
  const wipValues = stages.map((stage) => toFiniteNumber(stage.wip_limit)).filter((value): value is number => value !== null);
  const requireApproval = stages.some((stage) => Boolean(stage.require_approval));

  const uniqueWip = [...new Set(wipValues)];

  return {
    tp_standard: tpValues.length > 0 ? tpValues.reduce((sum, value) => sum + value, 0) : null,
    tra_standard: traValues.length > 0 ? traValues.reduce((sum, value) => sum + value, 0) : null,
    expected_error_percent:
      errorValues.length > 0 ? errorValues.reduce((sum, value) => sum + value, 0) / errorValues.length : null,
    correction_time_standard:
      correctionValues.length > 0 ? correctionValues.reduce((sum, value) => sum + value, 0) : null,
    warning_threshold_percent: thresholdValues.length > 0 ? Math.max(...thresholdValues) : 80,
    wip_limit: uniqueWip.length === 1 ? uniqueWip[0] : null,
    require_approval: requireApproval,
  };
}
